/**
 * Pestaña «Espiritual» de Nuevo/Editar Miembro — mismo UI para administrador, supervisor y líder.
 */
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { NativeSelect } from "@/components/nativeUi";
import {
  memberSpiritualFormLabels,
  YOUTH_CAMP_GROUP_OPTIONS,
} from "@/lib/memberFormLabels";
import { mentorNameForFormField } from "@/lib/memberMentorForm";

function NativeSwitch({ checked, onCheckedChange, id }) {
  return (
    <label className="relative inline-flex items-center cursor-pointer">
      <input
        type="checkbox"
        id={id}
        checked={checked || false}
        onChange={(e) => onCheckedChange(e.target.checked)}
        className="sr-only peer"
      />
      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600" />
    </label>
  );
}

/**
 * @param {{ formData: object, setFormData: import("react").Dispatch<import("react").SetStateAction<object>> }} props
 */
export function MemberSpiritualTabFields({ formData, setFormData }) {
  const hasMentor = Boolean(formData.has_mentor);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="flex items-center justify-between p-4 border rounded-lg">
        <div>
          <Label htmlFor="member-encuentro-attended">
            {memberSpiritualFormLabels.encounterAttended}
          </Label>
          <p className="text-sm text-gray-500">{memberSpiritualFormLabels.encounterSubtitle}</p>
        </div>
        <NativeSwitch
          id="member-encuentro-attended"
          checked={formData.encuentro_attended}
          onCheckedChange={(v) => setFormData({ ...formData, encuentro_attended: v })}
        />
      </div>
      {formData.encuentro_attended && (
        <div className="space-y-2">
          <Label>Fecha del Encuentro</Label>
          <Input
            type="date"
            value={formData.encuentro_date}
            onChange={(e) => setFormData({ ...formData, encuentro_date: e.target.value })}
          />
        </div>
      )}
      <div className="flex items-center justify-between p-4 border rounded-lg">
        <div>
          <Label>¿Está Bautizado?</Label>
          <p className="text-sm text-gray-500">Bautismo en agua</p>
        </div>
        <NativeSwitch
          checked={formData.baptized}
          onCheckedChange={(v) => setFormData({ ...formData, baptized: v })}
        />
      </div>
      {formData.baptized && (
        <div className="space-y-2">
          <Label>Fecha del Bautismo</Label>
          <Input
            type="date"
            value={formData.baptism_date}
            onChange={(e) => setFormData({ ...formData, baptism_date: e.target.value })}
          />
        </div>
      )}
      <div className="flex items-center justify-between p-4 border rounded-lg">
        <div>
          <Label>¿Tiene Mentor?</Label>
          <p className="text-sm text-gray-500">Acompañamiento espiritual</p>
        </div>
        <NativeSwitch
          checked={hasMentor}
          onCheckedChange={(v) =>
            setFormData((prev) => ({
              ...prev,
              has_mentor: v,
              mentor_name: v ? mentorNameForFormField(prev.mentor_name) : "",
            }))
          }
        />
      </div>
      {!hasMentor ? (
        <p className="text-sm text-gray-600 md:col-span-2">{memberSpiritualFormLabels.mentorNone}</p>
      ) : (
        <div className="space-y-2">
          <Label>{memberSpiritualFormLabels.mentorName}</Label>
          <Input
            value={mentorNameForFormField(formData.mentor_name)}
            onChange={(e) => setFormData({ ...formData, mentor_name: e.target.value })}
            placeholder="Nombre del mentor"
          />
        </div>
      )}
      <div className="flex items-center justify-between p-4 border rounded-lg">
        <div>
          <Label>¿Inscrito en Escuela de Líderes?</Label>
          <p className="text-sm text-gray-500">Formación de liderazgo</p>
        </div>
        <NativeSwitch
          checked={formData.leader_school_enrolled}
          onCheckedChange={(v) => setFormData({ ...formData, leader_school_enrolled: v })}
        />
      </div>
      <div className="flex items-center justify-between p-4 border rounded-lg">
        <div>
          <Label htmlFor="member-youth-camp-attended">
            {memberSpiritualFormLabels.youthCampAttended}
          </Label>
          <p className="text-sm text-gray-500">{memberSpiritualFormLabels.youthCampAttendedSubtitle}</p>
        </div>
        <NativeSwitch
          id="member-youth-camp-attended"
          checked={formData.youth_camp_attended}
          onCheckedChange={(v) =>
            setFormData((prev) => ({
              ...prev,
              youth_camp_attended: v,
              youth_camp_group: v ? prev.youth_camp_group : "",
            }))
          }
        />
      </div>
      {formData.youth_camp_attended && (
        <div className="space-y-2">
          <Label htmlFor="member-youth-camp-group">{memberSpiritualFormLabels.youthCampGroup}</Label>
          <NativeSelect
            id="member-youth-camp-group"
            value={formData.youth_camp_group || ""}
            onChange={(v) => setFormData({ ...formData, youth_camp_group: v })}
            placeholder="Seleccionar grupo"
          >
            {YOUTH_CAMP_GROUP_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </NativeSelect>
        </div>
      )}
    </div>
  );
}
